<template>
  <DefineTemplate>
    <div
      class="relative"
      v-bind="$attrs"
    >
      <div v-on-click-outside="hidePredictions">
        <div>
          <div
            ref="searchFieldEl"
            class="relative"
          >
            <TInput
              :id="inputId"
              ref="inputEl"
              v-model="search"
             
              name="search-place"
              type="text"
              autocomplete="off"
              :placeholder="inputPlaceholder"
              class="px-12 overflow-hidden text-ellipsis"
              @focus="showPredictions"
              @keyup.enter="keyUp"
            />

            <TIcon
              :icon="['far', 'search']"
              class="absolute left-4 top-1/2 transform -translate-y-1/2"
            />

            <div
              class="absolute right-4 top-1/2 transform -translate-y-1/2"
              :class="{ 'pointer-events-none': !searchPredictionsLoading && !(search && predictionsVisible) }"
            >
              <div v-if="searchPredictionsLoading">
                <AtomSpinner size="sm" />
              </div>
              <FormInputClearButton
                v-else-if="search && predictionsVisible"
                variant="muted"
                icon-size="sm"
                @click="clearPlace"
              />
            </div>
          </div>
        </div>

        <LocationPredictions
          v-if="isPredictionsShown"
          ref="predictionsEl"
          :predictions="searchPredictions"
          :style="isMdBreakpoint ? floatingStyles : undefined"
          class="relative overflow-hidden md:overflow-y-auto md:z-10 md:bg-white md:rounded-lg md:shadow-lg"
          @set-place="setPlace"
        />
      </div>
    </div>
  </DefineTemplate>

  <template v-if="isMobile">
    <button
      type="button"
     
      :class="[
        'px-4 py-3 flex items-center gap-2 w-full contain-inline-size border border-border-default rounded-md cursor-pointer',
        { 'text-text-default': destination },
        { 'text-text-muted': !destination },
      ]"
      @click="openDrawer"
    >
      <TIcon
        :icon="['far', 'search']"
        class="shrink-0"
      />
      <span class="truncate">{{ destination || inputPlaceholder }}</span>
    </button>

    <TModalCritical
      v-model:visible="drawerVisibility"
      position-drawer="top"
      size="full"
      :title="title || t('title')"
      @visible:content="focusInput"
    >
      <ReuseTemplate />
    </TModalCritical>
  </template>

  <ReuseTemplate v-else />
</template>

<script setup lang="ts">
// Possible to consolidate with `components/search/form/location.vue`?
import { vOnClickOutside } from '@vueuse/components'
import { createReusableTemplate } from '@vueuse/core'
import { autoUpdate, size, useFloating } from '@floating-ui/vue'
import type { COUNTRY_SHORTS } from '~/constants'
import type { BookingEstimateDestination, Nullable } from '~/types'
import { TInput } from '#components'

const props = withDefaults(defineProps<{
  inputId?: string
  inputPlaceholder: string
  destination?: string
  countries?: COUNTRY_SHORTS[]
  title?: string
}>(), {
  destination: '',
})

const emit = defineEmits<{
  'update-address': [value?: BookingEstimateDestination]
}>()

defineOptions({
  inheritAttrs: false,
})

const { t } = useI18n()

// On mobile the search is rendered inside a drawer, otherwise inline
const [DefineTemplate, ReuseTemplate] = createReusableTemplate()

const searchTimer: Ref<Nullable<ReturnType<typeof setTimeout>>> = ref(null)

const google = await useGoogleMaps()

const search = ref(props.destination)
const predictionsVisible = ref(false)
const searchPredictions: Ref<google.maps.places.PlacePrediction[]> = ref([])
const searchPredictionsLoading = ref(false)
const isSettingPlace = ref(false)
const drawerVisibility = ref(false)

const isPredictionsShown = computed(() => predictionsVisible.value && searchPredictions.value && searchPredictions.value.length > 0)

const { isMdBreakpoint } = useBreakpoint()
const searchFieldEl = useTemplateRef('searchFieldEl')
const predictionsEl = useTemplateRef<ComponentPublicInstance>('predictionsEl')

// On medium viewports the predictions float below the field. The fixed strategy keeps them out of any
// scrolling parent (e.g. a modal body), so they can overflow it instead of being clipped.
const { floatingStyles } = useFloating(computed(() => isMdBreakpoint.value ? searchFieldEl.value : null), predictionsEl, {
  strategy: 'fixed',
  placement: 'bottom-start',
  middleware: [
    size({
      padding: 16,
      apply({ rects, elements, availableHeight }) {
        Object.assign(elements.floating.style, {
          width: `${rects.reference.width}px`,
          maxHeight: `${availableHeight}px`,
        })
      },
    }),
  ],
  whileElementsMounted: autoUpdate,
})

function showPredictions() {
  if (search.value && searchPredictions.value.length === 0) {
    getPlacesPredictions(search.value)
  }
  else {
    predictionsVisible.value = true
  }
}

function hidePredictions() {
  predictionsVisible.value = false
}

const runtimeConfig = useRuntimeConfig()
function getPlacesPredictions(searchText: string) {
  search.value = searchText
  searchPredictions.value = []
  searchPredictionsLoading.value = false
  predictionsVisible.value = false

  if (!searchText) {
    return
  }

  searchPredictionsLoading.value = true

  if (searchTimer.value) {
    clearTimeout(searchTimer.value)
  }

  searchTimer.value = setTimeout(async () => {
    // Improvement: Add location bias
    const predictions = await google?.placePredictions(searchText, props?.countries?.length ? props.countries : runtimeConfig.public.supportedCountries)
    searchPredictions.value = predictions || []
    searchPredictionsLoading.value = false
    predictionsVisible.value = Boolean(search.value)
  }, 500)
}

const { $gis } = useNuxtApp()

async function setPlace(place: google.maps.places.PlacePrediction) {
  isSettingPlace.value = true

  search.value = place.mainText?.text ?? ''

  try {
    const placeDetails = await $gis('/Place/Details', { query: { PlaceId: place.placeId, Fields: 'address_components,geometry,types' } })

    if (placeDetails) {
      const destinationData: BookingEstimateDestination = {
        latitude: placeDetails.coordinates?.lat ?? 0,
        longitude: placeDetails.coordinates?.lng ?? 0,
        types: place.types ?? [],
        name: place?.text?.text ?? '',
        placeId: place?.placeId ?? '',
        country: (placeDetails.countryCode || placeDetails.code) as COUNTRY_SHORTS,
      }

      emit('update-address', destinationData)
      drawerVisibility.value = false
    }
  }
  catch (_error) {
    emit('update-address')
  }

  // This prevents running another search immediately when the place is set.
  nextTick(() => {
    predictionsVisible.value = false
    isSettingPlace.value = false
  })
}

function clearPlace() {
  search.value = ''
  searchPredictions.value = []
  emit('update-address')
}

function keyUp() {
  if (predictionsVisible.value && searchPredictions.value.length > 0 && searchPredictions.value[0]) {
    setPlace(searchPredictions.value[0])
  }
}

watch(search, (newSearchValue) => {
  predictionsVisible.value = false

  if (isResettingSearch.value) {
    return
  }

  if (!newSearchValue) {
    clearPlace()
  }

  if (newSearchValue && !isSettingPlace.value) {
    getPlacesPredictions(newSearchValue)
  }
})

watch(() => props.destination, () => {
  if (search.value === props.destination) {
    return
  }

  isSettingPlace.value = true
  searchPredictions.value = []

  search.value = props.destination

  nextTick(() => {
    isSettingPlace.value = false
  })
}, { immediate: true })

const isResettingSearch = ref(false)

function openDrawer() {
  // Discard a search left over from a drawer closed without selecting a place, without emitting
  if (search.value !== props.destination) {
    isResettingSearch.value = true
    search.value = props.destination

    nextTick(() => {
      isResettingSearch.value = false
    })
  }

  searchPredictions.value = []
  predictionsVisible.value = false
  drawerVisibility.value = true
}

const { isMobile } = useDevice()
const inputEl = ref<ComponentPublicInstance<typeof TInput>>()

function focusInput() {
  if (!inputEl.value) {
    return
  }

  focusElementInput(inputEl.value.$el, true)
}
</script>

<i18n lang="json">
  {
    "en": {
      "title": "Location"
    },
    "fr": {
      "title": "Sélectionner un endroit"
    }
  }
</i18n>
